<template>
  <v-expansion-panels v-model="recommendedExpansionPanel">
    <v-expansion-panel v-for="({ courses, jointCredits, notes, showSearch }, index) in recommendedCourses" :key="'rec-'+index" :disabled="courses.length === 1 && (courses[0].course === 'COR100' || (courses[0].options.length === 0 && !notes))">
      <v-expansion-panel-header>
        <div>
          <v-icon v-if="hasCourse(courses)" color="success" class="mr-1">fas fa-check-circle</v-icon>
          <v-chip v-for="{ course } in courses" :key="course" label outlined class="mr-1">{{ course }}</v-chip>
          <span v-if="jointCredits.length > 0" class="ml-2">{{ jointCredits.join(' or ') }} Credit{{ jointCredits.length === 1 && jointCredits[0] === 1 ? '' : 's' }}</span>
          <span v-if="courses[0] === 'COR100'" class="ml-2">This course is required</span>
        </div>
      </v-expansion-panel-header>
      <v-expansion-panel-content class="no-padding">
        <v-alert v-if="notes" type="info" border="left" class="mb-2 mx-2" elevation="3" colored-border>
          <template v-if="showSearch" v-slot:append>
            <v-btn class="hidden-md-and-up" small fab style="margin-right:-8px" @click="$emit('search')">
              <v-icon small>fal fa-search</v-icon>
            </v-btn>
            <v-btn class="hidden-sm-and-down" @click="$emit('search')">
              <v-icon left>fal fa-search</v-icon>
              Course Search
            </v-btn>
          </template>
          <div v-html="notes" />
        </v-alert>
        <v-card v-if="courses[0].options.length > 0" elevation="1" class="ma-2">
          <v-list>
            <v-list-group v-for="{ course, title, credits, options } in courses" :key="'group-' + course" :value="courses.length === 1 ? true : false">
              <template v-slot:activator>
                <v-subheader class="pl-0">{{ title }} - {{ credits }} Credit{{ credits === 1 ? '' : 's' }}</v-subheader>
              </template>
              <recommended-entry v-for="entry in options" :key="entry.crn" :entry="entry" :schedule="schedule" @add="addCourse(entry)" />
            </v-list-group>
          </v-list>
        </v-card>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>
<style>
div.v-expansion-panel-content.no-padding > div.v-expansion-panel-content__wrap {
  padding: 0
}
</style>
<script>
import { ref, watch } from '@vue/composition-api'
import { hasOverlap } from './functions'

export default {
  props: {
    major: {
      type: String,
      required: true
    },
    schedule: {
      type: Array,
      default: () => { return [] }
    },
    courseSearchFilter: {
      type: Array,
      required: true
    }
  },
  components: {
    RecommendedEntry: () => import('./recommendedEntry')
  },
  setup (props, { root, emit }) {
    const recommendedExpansionPanel = ref(null)
    const recommendedCourses = ref([])

    watch(() => props.major, async (major) => {
      if (root.$store.state.term !== '' && major != null && major !== '') {
        // Load the recommended courses for the selected major
        const { data } = await root.$feathers.service('student/reg-setting').find({ query: { term: root.$store.state.term, form: 'prereg', type: 'curricula', major } })
        recommendedCourses.value = []
        if (data.length > 0) {
          const { options } = data[0]
          if (['PV', 'LL'].includes(root.$store.state.academics.admitCode)) {
            options.splice(1, 0, { course: ['GE131'], notes: '' })
          }
          for (const { course: courses, notes } of data[0].options) {
            let temp = { courses: [], notes: notes.replaceAll('\n', '<br/>'), jointCredits: [] }
            const aggregate = [
              { $match: { term: root.$store.state.term } },
              { $addFields: { sortMeet: { $first: '$meetingBase' } } },
              { $match: { 'sortMeet.startTime': { $ne: '' } } },
              { $addFields: {
                sortDays: '$sortMeet.days',
                sortAmPm: {
                  $substr: ['$sortMeet.startTime', { $subtract: [{ $strLenCP: '$sortMeet.startTime' }, 2] }, 2]
                },
                sortHour: {
                  $toInt: {
                    $replaceAll: {
                      input: { $substr: ['$sortMeet.startTime', 0, 2] },
                      find: ':',
                      replacement: ''
                    }
                  }
                }
              } },
              { $sort: {
                sortDays: 1,
                sortAmPm: 1,
                sortHour: 1
              } }
            ]
            let hasEmptyCourseList = false
            for (const course of courses) {
              aggregate[0].$match.title = { $regex: course + ' ' }
              let { data: options } = await root.$feathers.service('calendar/classes').find({ query: { aggregate } })
              if (options.length > 0 && !temp.jointCredits.includes(options[0].credits)) temp.jointCredits.push(options[0].credits)
              const obj = { course, title: course, options, credits: (options.length > 0 ? options[0].credits : 'Variable') }
              if (options.length === 0) hasEmptyCourseList = true
              else obj.title = options[0].title.replace(/Section [0-9]{1,2}/, '')
              temp.courses.push(obj)
            }
            if (hasEmptyCourseList && notes === '') {
              temp.notes = 'See the pre-registration guide pages 13-16 for a list of courses.'
              temp.showSearch = true
            }
            temp.jointCredits.sort()
            recommendedCourses.value.push(temp)
          }
        }
      }
    })

    function addCourse (course) {
      recommendedExpansionPanel.value = null
      emit('addCourse', course)
    }

    function hasCourse (courses) {
      return props.schedule.filter(({ title }) => courses.filter(({ course }) => course === title.substr(0, title.indexOf(' '))).length > 0).length > 0
    }

    function hasConflict (course) {
      if (!('meetingBase' in course)) return false
      for (const schedCourse of props.schedule) {
        if (!('meetingBase' in schedCourse)) continue
        if (hasOverlap(course, schedCourse)) return true
      }
      return false
    }

    return {
      recommendedExpansionPanel,
      recommendedCourses,
      addCourse,
      hasCourse,
      hasConflict,
      hasOverlap
    }
  }
}
</script>
